<template>
  <div class="mobile-cart-drawer" v-if="visible" @click.self="close">
    <div class="mobile-cart-content" @click.stop>
      <header class="mobile-cart-header">
        <h2 class="mobile-cart-title">Shopping Cart</h2>
        <button class="mobile-cart-close" @click="close">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </header>

      <div v-if="items.length" class="mobile-cart-items">
        <div
          v-for="(item, index) in items"
          :key="`cart-item-${index}`"
          class="mobile-cart-item"
        >
          <div class="mobile-cart-item-info">
            <h4 class="mobile-cart-item-name">{{ getItemName(item) }}</h4>
            <p class="mobile-cart-item-price">R{{ formatPrice(getItemPrice(item)) }}</p>
          </div>
          <div class="mobile-cart-item-controls">
            <button
              class="mobile-cart-btn-qty"
              @click="$emit('decrease', index)"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            </button>
            <span class="mobile-cart-quantity">{{ item.quantity }}</span>
            <button
              class="mobile-cart-btn-qty"
              @click="$emit('increase', index)"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
            </button>
          </div>
          <button
            class="mobile-cart-remove"
            @click="$emit('remove', index)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
          </button>
        </div>
      </div>

      <div v-else class="mobile-cart-empty">
        <svg class="mobile-cart-empty-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.15.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>
        <p>Your cart is empty</p>
        <button class="mobile-cart-shop-btn" @click="close">Continue Shopping</button>
      </div>

      <footer v-if="items.length" class="mobile-cart-footer">
        <div class="mobile-cart-total">
          <span>Total</span>
          <span class="mobile-cart-total-amount">R{{ formatPrice(total) }}</span>
        </div>
        <button
          class="mobile-cart-checkout-btn"
          @click="$emit('checkout')"
        >
          Proceed to Checkout
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileCartDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    formatPrice: {
      type: Function,
      required: true
    },
    getItemPrice: {
      type: Function,
      required: true
    }
  },
  emits: ['close', 'increase', 'decrease', 'remove', 'checkout'],
  methods: {
    close() {
      this.$emit('close')
    },
    getItemName(item) {
      return typeof item === 'string' ? item : item?.product || item?.name || 'Unknown Product'
    }
  }
}
</script>

<style scoped>
.mobile-cart-drawer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  animation: fadeIn 0.2s ease;
}

.mobile-cart-content {
  width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 1.5rem 1.5rem 0 0;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

.mobile-cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.mobile-cart-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.mobile-cart-close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-cart-close svg {
  width: 1.5rem;
  height: 1.5rem;
  fill: #6b7280;
}

.mobile-cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-cart-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.75rem;
}

.mobile-cart-item-info {
  flex: 1;
}

.mobile-cart-item-name {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
}

.mobile-cart-item-price {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: #52CD36;
}

.mobile-cart-item-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  padding: 0.25rem;
}

.mobile-cart-btn-qty {
  background: none;
  border: none;
  padding: 0.375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: background 0.2s;
}

.mobile-cart-btn-qty:hover {
  background: #f3f4f6;
}

.mobile-cart-btn-qty svg {
  width: 1.125rem;
  height: 1.125rem;
  fill: #52CD36;
}

.mobile-cart-quantity {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  min-width: 1.5rem;
  text-align: center;
}

.mobile-cart-remove {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-cart-remove svg {
  width: 1.25rem;
  height: 1.25rem;
  fill: #ef4444;
}

.mobile-cart-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
}

.mobile-cart-empty-icon {
  width: 4rem;
  height: 4rem;
  fill: #9ca3af;
  margin-bottom: 1rem;
}

.mobile-cart-empty p {
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 1.5rem;
}

.mobile-cart-shop-btn {
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #52CD36 0%, #3da026 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.mobile-cart-footer {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.mobile-cart-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-family: 'Montserrat', sans-serif;
}

.mobile-cart-total span:first-child {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.mobile-cart-total-amount {
  font-size: 1.5rem;
  font-weight: 800;
  color: #52CD36;
}

.mobile-cart-checkout-btn {
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #52CD36 0%, #3da026 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.mobile-cart-checkout-btn:active {
  transform: scale(0.98);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>





